import {
  AutoTagOutcome,
  AutoTagTableStatus,
  AutoTagTaskStatusValue,
} from "@/apis/table/route";

/**
 * 백엔드 tool/단계 이름 → 사용자용 한국어 라벨.
 * 검색 도구 외에 진행 중 current_tool 로 노출되는 내부 단계(references/llm_response 등)도 포함.
 */
export const TOOL_LABELS: Record<string, string> = {
  search_role_tags: "주석 목차 내 태그 검색",
  search_all_tags: "전체 태그 검색",
  search_company_extended_tags: "확장 태그 검색",
  recommend_tags_by_cell: "AI 추천 태그 검색",
  references: "참조할 표 찾는 중",
  llm_response: "태그 분석 중",
  conversation: "마무리 중",
};

/** 미매핑 이름은 영어를 노출하지 않고 일반 문구로 대체한다. */
export const toolLabel = (tool: string | null): string =>
  tool ? (TOOL_LABELS[tool] ?? "분석 중") : "분석 중";

export type AutoTagDisplayState =
  | "NOT_STARTED"
  | "PENDING"
  | "RUNNING"
  | "SUCCESS"
  | "NEEDS_REVIEW"
  | "EMPTY"
  | "ALREADY_TAGGED"
  | "AXIS_FALLBACK"
  | "SKIPPED"
  | "FAILED";

/** lifecycle status + outcome 을 화면용 단일 상태로 합친다. status null 은 미실행. */
export const resolveDisplayState = (
  status: AutoTagTaskStatusValue | null,
  outcome: AutoTagOutcome | null,
): AutoTagDisplayState => {
  if (status === null) return "NOT_STARTED";
  if (status === "PENDING") return "PENDING";
  if (status === "RUNNING") return "RUNNING";
  if (status === "FAILED") return "FAILED";
  // COMPLETED → outcome 으로 세분화
  if (outcome === "ERROR") return "FAILED";
  if (outcome === "NEEDS_REVIEW") return "NEEDS_REVIEW";
  if (outcome === "EMPTY") return "EMPTY";
  if (outcome === "ALREADY_TAGGED") return "ALREADY_TAGGED";
  if (outcome === "AXIS_FALLBACK") return "AXIS_FALLBACK";
  if (outcome === "SKIPPED") return "SKIPPED";
  return "SUCCESS";
};

/**
 * 일반(비-debug) 모드 표시용으로 상태를 접는다.
 * - 축대체(AXIS_FALLBACK)는 사용자에게 성공으로 취급한다.
 * - 태그 없음(EMPTY)은 검토(NEEDS_REVIEW)로 묶는다 — "하나도 못 붙임"과 "붙였지만
 *   미완"은 사용자가 할 일(표를 열어 태깅 확인)이 같다.
 * debug 모드면 원래 상태를 그대로 노출한다.
 */
export const foldDisplayState = (
  state: AutoTagDisplayState,
  debug: boolean,
): AutoTagDisplayState => {
  if (debug) return state;
  if (state === "AXIS_FALLBACK") return "SUCCESS";
  if (state === "EMPTY") return "NEEDS_REVIEW";
  return state;
};

/**
 * 표시 상태별 표 개수. 일반 모드는 fold 된 상태로 센다 — 그래서 일반 모드의 NEEDS_REVIEW
 * 는 EMPTY 까지 합친 수이고, debug 모드는 둘을 따로 센다.
 */
export const countDisplayStates = (
  tables: Pick<AutoTagTableStatus, "status" | "outcome">[],
  debug: boolean,
): Map<AutoTagDisplayState, number> => {
  const counts = new Map<AutoTagDisplayState, number>();
  tables.forEach((t) => {
    const state = foldDisplayState(
      resolveDisplayState(t.status, t.outcome),
      debug,
    );
    counts.set(state, (counts.get(state) ?? 0) + 1);
  });
  return counts;
};

export const STATE_LABELS: Record<AutoTagDisplayState, string> = {
  NOT_STARTED: "미실행",
  PENDING: "대기 중",
  RUNNING: "진행 중",
  SUCCESS: "성공",
  NEEDS_REVIEW: "검토",
  EMPTY: "적절한 태그를 찾지 못했습니다.",
  ALREADY_TAGGED: "이미 태깅되어 있습니다.",
  AXIS_FALLBACK: "일부 축을 기본값으로 대체했습니다.",
  SKIPPED: "건너뜀",
  FAILED: "실패",
};

export const STATE_CHIP_CLASS: Record<AutoTagDisplayState, string> = {
  NOT_STARTED: "bg-neutral-100 text-neutral-400",
  PENDING: "bg-neutral-100 text-neutral-500",
  RUNNING: "bg-blue-50 text-blue-600",
  SUCCESS: "bg-green-50 text-green-600",
  NEEDS_REVIEW: "bg-orange-50 text-orange-600",
  EMPTY: "bg-amber-50 text-amber-600",
  ALREADY_TAGGED: "bg-sky-50 text-sky-600",
  // debug 에서만 보이는 상태라, 일반 모드의 검토(주황)와 겹치지 않는 색을 쓴다.
  AXIS_FALLBACK: "bg-violet-50 text-violet-600",
  SKIPPED: "bg-neutral-100 text-neutral-500",
  FAILED: "bg-red-50 text-red-600",
};

/** "31-3 특수관계자" → 섹션번호 [31, 3] + 라벨. 번호가 없으면 nums=[]. */
const parseTableSection = (name: string): { nums: number[]; label: string } => {
  const match = name.match(/^(\d+(?:-\d+)*)\s+(.*)$/);
  if (!match) return { nums: [], label: name };
  return {
    nums: match[1].split("-").map((n) => Number.parseInt(n, 10)),
    label: match[2],
  };
};

/**
 * 표 이름을 섹션번호("31-3", "9", "10") 기준으로 정렬한다.
 *
 * 섹션번호를 "-" 로 split 해 숫자 튜플로 비교하므로 "9" < "10" 이 보장된다(문자열
 * 비교는 "10" < "9"). 같은 섹션이면 라벨을 한국어 locale 로 비교하고, 번호가 없는
 * 이름은 뒤로 보낸다.
 */
export const compareTableName = (a: string, b: string): number => {
  const pa = parseTableSection(a);
  const pb = parseTableSection(b);
  if (pa.nums.length === 0 && pb.nums.length > 0) return 1;
  if (pa.nums.length > 0 && pb.nums.length === 0) return -1;
  const len = Math.min(pa.nums.length, pb.nums.length);
  for (let i = 0; i < len; i += 1) {
    if (pa.nums[i] !== pb.nums[i]) return pa.nums[i] - pb.nums[i];
  }
  if (pa.nums.length !== pb.nums.length) {
    return pa.nums.length - pb.nums.length;
  }
  return a.localeCompare(b, "ko", { numeric: true });
};
